import React, { useCallback, useEffect, useState } from 'react';
import { COGGINS_VIEW_EVENT, type CogginsViewerPayload } from '../../utils/horseCareApi.ts';

function isPdfType(contentType: string, fileName: string): boolean {
  return contentType.includes('pdf') || /\.pdf$/i.test(fileName);
}

function isRasterImageType(contentType: string, fileName: string): boolean {
  if (/^image\/(jpeg|jpg|png|gif|webp)$/i.test(contentType)) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(fileName);
}

function isHeicType(contentType: string, fileName: string): boolean {
  return /heic|heif/i.test(contentType) || /\.heic$|\.heif$/i.test(fileName);
}

export default function CogginsDocumentViewerHost() {
  const [view, setView] = useState<CogginsViewerPayload | null>(null);

  const close = useCallback(() => {
    setView(current => {
      if (current?.blobUrl) URL.revokeObjectURL(current.blobUrl);
      return null;
    });
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CogginsViewerPayload>).detail;
      if (!detail?.blobUrl) return;
      setView(prev => {
        if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return detail;
      });
    };
    window.addEventListener(COGGINS_VIEW_EVENT, handler);
    return () => window.removeEventListener(COGGINS_VIEW_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!view) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, close]);

  if (!view) return null;

  const showPdf = isPdfType(view.contentType, view.fileName);
  const showImage = !showPdf && isRasterImageType(view.contentType, view.fileName);
  const showHeic = !showPdf && !showImage && isHeicType(view.contentType, view.fileName);

  const downloadHeic = () => {
    const anchor = document.createElement('a');
    anchor.href = view.blobUrl;
    anchor.download = view.fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const openInNewTab = () => {
    window.open(view.blobUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/85" role="dialog" aria-modal="true" aria-label="Coggins document">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-900 text-white shrink-0">
        <p className="text-sm font-medium truncate min-w-0">{view.fileName}</p>
        <div className="flex items-center gap-2 shrink-0">
          {showPdf && (
            <button
              type="button"
              onClick={openInNewTab}
              className="rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-medium min-h-[44px]"
            >
              Open in new tab
            </button>
          )}
          <button
            type="button"
            onClick={close}
            className="rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-medium min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-3 sm:p-6 flex flex-col items-stretch justify-center overflow-hidden">
        {showPdf && (
          <iframe
            title={view.fileName}
            src={`${view.blobUrl}#view=FitH`}
            className="flex-1 w-full min-h-[60vh] max-w-5xl mx-auto bg-white rounded-lg shadow-lg border-0"
          />
        )}
        {showImage && (
          <img
            src={view.blobUrl}
            alt={view.fileName}
            className="max-w-full max-h-[calc(100vh-5rem)] object-contain rounded-lg shadow-lg bg-white mx-auto"
          />
        )}
        {showHeic && (
          <div className="max-w-md text-center text-white space-y-4 p-6 mx-auto">
            <p className="text-sm text-white/90">
              iPhone HEIC photos cannot be previewed in most browsers. Download the file to open it on your device, or
              upload a PDF or JPEG copy for in-browser viewing.
            </p>
            <button
              type="button"
              onClick={downloadHeic}
              className="bg-ggaa-green text-white px-4 py-3 rounded-lg font-medium min-h-[44px]"
            >
              Download file
            </button>
          </div>
        )}
        {!showPdf && !showImage && !showHeic && (
          <div className="max-w-md text-center text-white space-y-4 p-6 mx-auto">
            <p className="text-sm">This file type cannot be previewed here.</p>
            <a
              href={view.blobUrl}
              download={view.fileName}
              className="inline-block bg-ggaa-green text-white px-4 py-3 rounded-lg font-medium min-h-[44px]"
            >
              Download file
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

